import type { KnowledgeBase } from './types';

export const LAB_PIPELINE_ERROR_SECTION_NAME = '__pipeline_error';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

interface PipelineTarget {
  sections?: Record<string, unknown>;
}

export function getLabPipelineFailure(target?: PipelineTarget | null): string | null {
  const section = target?.sections?.[LAB_PIPELINE_ERROR_SECTION_NAME];
  if (!isRecord(section)) return null;
  const message = typeof section.original === 'string' ? section.original.trim() : '';
  return message || null;
}

export function getKnowledgePipelineFailure(kb?: KnowledgeBase | null): string | null {
  return getLabPipelineFailure(kb);
}

export function getStaleContentPipelineFailure(
  content?: (PipelineTarget & { status?: string; updatedAt?: string | null }) | null,
  now = Date.now(),
): string | null {
  const persisted = getLabPipelineFailure(content);
  if (persisted) return persisted;

  const pipelineState = content?.sections?.__pipeline_state;
  const state = isRecord(pipelineState) && typeof pipelineState.original === 'string'
    ? pipelineState.original
    : '';
  if (state === 'error') return 'Generowanie zostało przerwane. Ukończone sekcje zostały zachowane.';
  if (state !== 'generating') return null;

  const updatedAt = content?.updatedAt ? Date.parse(content.updatedAt) : Number.NaN;
  if (!Number.isFinite(updatedAt)) return null;
  const idleMs = Math.max(0, now - updatedAt);

  if (content?.status === 'DRAFT' && idleMs >= 15_000) {
    return 'Generowanie zostało przerwane. Ukończone sekcje zostały zachowane i możesz wznowić proces.';
  }
  if (content?.status === 'GENERATING' && idleMs >= 12 * 60_000) {
    return 'Generowanie nie odpowiada od ponad 12 minut. Ukończone sekcje zostały zachowane i możesz wznowić proces.';
  }
  return null;
}

export function hasKnowledgePipelineProgress(kb?: KnowledgeBase | null): boolean {
  if (!kb?.sections || !isRecord(kb.sections)) return false;
  return Object.entries(kb.sections).some(([name, section]) => {
    if (name === LAB_PIPELINE_ERROR_SECTION_NAME || !isRecord(section)) return false;
    return typeof section.original === 'string' && Boolean(section.original.trim());
  });
}

export function isLabPipelineCancelled(target?: PipelineTarget | null): boolean {
  const state = target?.sections?.__pipeline_state;
  return isRecord(state) && state.original === 'cancelled';
}
